import React, { useState, useEffect } from "react";
import RegistrationRow from "./RegistrationRow";
import LoadingSpinner from "../common/LoadingSpinner";
import ErrorMessage from "../common/ErrorMessage";

/**
 * RegistrationTable component displays all registrations in a table format
 * Provides filtering, sorting, and pagination functionality
 */
const RegistrationTable = ({
  registrations,
  onApprove,
  onReject,
  isLoading,
  error,
  onRefresh,
}) => {
  const [filteredRegistrations, setFilteredRegistrations] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [sortField, setSortField] = useState("createdAt");
  const [sortDirection, setSortDirection] = useState("desc");

  // Filter and sort registrations
  useEffect(() => {
    let filtered = [...registrations];

    // Apply status filter
    if (statusFilter !== "all") {
      filtered = filtered.filter((reg) => reg.status === statusFilter);
    }

    // Apply search filter
    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (reg) =>
          reg.userInfo?.fullName?.toLowerCase().includes(searchLower) ||
          reg.userInfo?.email?.toLowerCase().includes(searchLower) ||
          reg.userInfo?.mobileNumber?.includes(searchTerm),
      );
    }

    // Apply sorting
    filtered.sort((a, b) => {
      let aValue, bValue;

      switch (sortField) {
        case "fullName":
          aValue = a.userInfo?.fullName || "";
          bValue = b.userInfo?.fullName || "";
          break;
        case "email":
          aValue = a.userInfo?.email || "";
          bValue = b.userInfo?.email || "";
          break;
        case "status":
          aValue = a.status || "";
          bValue = b.status || "";
          break;
        case "createdAt":
        default:
          aValue = a.createdAt?.toDate?.() || new Date(a.createdAt || 0);
          bValue = b.createdAt?.toDate?.() || new Date(b.createdAt || 0);
          break;
      }

      if (aValue < bValue) return sortDirection === "asc" ? -1 : 1;
      if (aValue > bValue) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });

    setFilteredRegistrations(filtered);
  }, [registrations, statusFilter, searchTerm, sortField, sortDirection]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  const getSortIcon = (field) => {
    if (sortField !== field) return "↕️";
    return sortDirection === "asc" ? "↑" : "↓";
  };

  const getStatusCounts = () => {
    const counts = {
      all: registrations.length,
      pending: registrations.filter((r) => r.status === "pending").length,
      approved: registrations.filter((r) => r.status === "approved").length,
      rejected: registrations.filter((r) => r.status === "rejected").length,
    };
    return counts;
  };

  const statusCounts = getStatusCounts();

  if (error) {
    return (
      <div className="registration-table-container">
        <ErrorMessage message={error} />
        <button onClick={onRefresh} className="refresh-btn">
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="registration-table-container">
      {/* Table Controls */}
      <div className="table-controls">
        <div className="table-filters">
          <div className="search-field">
            <input
              type="text"
              placeholder="Search by name, email, or phone..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
          </div>

          <div className="status-filter">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="status-select"
            >
              <option value="all">All ({statusCounts.all})</option>
              <option value="pending">Pending ({statusCounts.pending})</option>
              <option value="approved">
                Approved ({statusCounts.approved})
              </option>
              <option value="rejected">
                Rejected ({statusCounts.rejected})
              </option>
            </select>
          </div>
        </div>

        <div className="table-actions">
          <button
            onClick={onRefresh}
            className="refresh-btn"
            disabled={isLoading}
          >
            {isLoading ? "Refreshing..." : "Refresh"}
          </button>
        </div>
      </div>

      {/* Results Summary */}
      <div className="results-summary">
        Showing {filteredRegistrations.length} of {registrations.length}{" "}
        registrations
      </div>

      {/* Table */}
      <div className="table-wrapper">
        {isLoading && registrations.length === 0 ? (
          <div className="table-loading">
            <LoadingSpinner />
            <p>Loading registrations...</p>
          </div>
        ) : filteredRegistrations.length === 0 ? (
          <div className="no-results">
            <p>No registrations found matching your criteria.</p>
          </div>
        ) : (
          <table className="registration-table">
            <thead>
              <tr>
                <th style={{ width: "48px", textAlign: "center" }}>#</th>
                <th
                  className="sortable-header"
                  onClick={() => handleSort("fullName")}
                >
                  Full Name {getSortIcon("fullName")}
                </th>
                <th
                  className="sortable-header"
                  onClick={() => handleSort("email")}
                >
                  Email {getSortIcon("email")}
                </th>
                <th>Mobile Number</th>
                <th>Country</th>
                <th
                  className="sortable-header"
                  onClick={() => handleSort("status")}
                >
                  Status {getSortIcon("status")}
                </th>
                <th>Receipt</th>
                <th
                  className="sortable-header"
                  onClick={() => handleSort("createdAt")}
                >
                  Created {getSortIcon("createdAt")}
                </th>
                <th>SMS Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredRegistrations.map((registration, index) => (
                <RegistrationRow
                  key={registration.id}
                  registration={registration}
                  serialNumber={index + 1}
                  onApprove={onApprove}
                  onReject={onReject}
                  isLoading={isLoading}
                />
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default RegistrationTable;
